import React from 'react';

/**
 * Auth Page Styles
 *
 * Reusable styles for authentication pages (login, password reset, invitations, etc.)
 *
 * Usage Philosophy:
 * - Consistent styling for all authentication flows
 * - Pre-composed layouts for full-page centered auth forms
 * - Optimized for mobile and desktop experiences
 *
 * Common Patterns:
 * - Login pages: Use pageContainer + cardContainer
 * - Password reset: Use pageContainer + cardContainer + errorAlert
 * - Success messages: Use pageContainer + cardContainerCentered + successEmoji
 * - Loading states: Use pageContainer + cardContainerCentered + loadingEmoji
 */

// ============================================================================
// PAGE LAYOUTS
// ============================================================================

export const authPageStyles = {
  /**
   * Page Container - Full viewport centered layout
   * Use for: Wrapping entire auth page
   */
  pageContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--background-color)',
  } as React.CSSProperties,

  /**
   * Card Container - Auth form card
   * Use for: Containing auth forms and content
   */
  cardContainer: {
    width: '100%',
    maxWidth: '400px',
    padding: '2rem',
    backgroundColor: 'var(--surface-color)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-md)',
  } as React.CSSProperties,

  /**
   * Card Container (Centered) - Auth form card with centered text
   * Use for: Success messages, loading states, error pages
   */
  cardContainerCentered: {
    width: '100%',
    maxWidth: '400px',
    padding: '2rem',
    backgroundColor: 'var(--surface-color)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-md)',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  /**
   * Wide Card Container - Wider auth form card
   * Use for: Multi-column forms, detailed setup pages
   */
  wideCardContainer: {
    width: '100%',
    maxWidth: '600px',
    padding: '2rem',
    backgroundColor: 'var(--surface-color)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-md)',
  } as React.CSSProperties,
};

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const authTextStyles = {
  /**
   * Page Title - Main heading for auth pages
   * Use for: "Sign In", "Reset Password", etc.
   */
  title: {
    fontSize: '1.5rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: 'var(--text-primary)',
  } as React.CSSProperties,

  /**
   * Page Title (Centered) - Main heading centered
   * Use for: Success messages, error pages
   */
  titleCentered: {
    fontSize: '1.5rem',
    fontWeight: '600',
    marginBottom: '1rem',
    color: 'var(--text-primary)',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  /**
   * Large Title - Prominent heading
   * Use for: Welcome pages, important messages
   */
  largeTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    marginBottom: '1rem',
    color: 'var(--text-primary)',
  } as React.CSSProperties,

  /**
   * Description Text - Secondary text below title
   * Use for: Instructions, explanations
   */
  description: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    marginBottom: '1.5rem',
  } as React.CSSProperties,

  /**
   * Description Text (Small Margin) - Tighter spacing
   * Use for: Brief descriptions
   */
  descriptionSmall: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Helper Text - Small instructional text
   * Use for: Field hints, format examples
   */
  helperText: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    marginTop: '0.25rem',
  } as React.CSSProperties,

  /**
   * Caption Text - Very small supporting text
   * Use for: Additional context, fine print
   */
  caption: {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    marginTop: '0.5rem',
  } as React.CSSProperties,
};

// ============================================================================
// ALERTS & FEEDBACK
// ============================================================================

export const authAlertStyles = {
  /**
   * Error Alert - Red error message box
   * Use for: Form errors, validation failures
   */
  error: {
    padding: '0.75rem',
    marginBottom: '1rem',
    backgroundColor: 'var(--alert-error-bg)',
    border: '1px solid var(--alert-error-border)',
    borderRadius: 'var(--radius)',
    color: 'var(--alert-error-text)',
    fontSize: '0.875rem',
  } as React.CSSProperties,

  /**
   * Success Alert - Green success message box
   * Use for: Success confirmations
   */
  success: {
    padding: '0.75rem',
    marginBottom: '1rem',
    backgroundColor: 'var(--alert-success-bg)',
    border: '1px solid var(--alert-success-border)',
    borderRadius: 'var(--radius)',
    color: 'var(--alert-success-text)',
    fontSize: '0.875rem',
  } as React.CSSProperties,

  /**
   * Info Alert - Blue information message box
   * Use for: Informational messages, tips
   */
  info: {
    padding: '0.75rem',
    marginBottom: '1rem',
    backgroundColor: 'var(--alert-info-bg)',
    border: '1px solid var(--alert-info-border)',
    borderRadius: 'var(--radius)',
    color: 'var(--alert-info-text)',
    fontSize: '0.875rem',
  } as React.CSSProperties,

  /**
   * Warning Alert - Yellow warning message box
   * Use for: Warnings, cautions
   */
  warning: {
    padding: '0.75rem',
    marginBottom: '1rem',
    backgroundColor: 'var(--alert-warning-bg)',
    border: '1px solid var(--alert-warning-border)',
    borderRadius: 'var(--radius)',
    color: 'var(--alert-warning-text)',
    fontSize: '0.875rem',
  } as React.CSSProperties,
};

// ============================================================================
// VISUAL ELEMENTS
// ============================================================================

export const authVisualStyles = {
  /**
   * Loading Emoji - Large emoji for loading states
   * Use for: "Validating...", "Processing..."
   */
  loadingEmoji: {
    fontSize: '2rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Success Emoji - Large emoji for success messages
   * Use for: "Email sent!", "Password updated!"
   */
  successEmoji: {
    fontSize: '3rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Error Emoji - Large emoji for error states
   * Use for: "Access denied", "Link expired"
   */
  errorEmoji: {
    fontSize: '3rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Icon Container - Medium emoji/icon display
   * Use for: General purpose icons
   */
  icon: {
    fontSize: '2.5rem',
    marginBottom: '1rem',
    color: 'var(--primary-color)',
  } as React.CSSProperties,
};

// ============================================================================
// BUTTON OVERRIDES FOR AUTH PAGES
// ============================================================================

export const authButtonStyles = {
  /**
   * Full Width Button - Button spanning full container width
   * Use for: Primary actions in auth forms
   */
  fullWidth: {
    width: '100%',
  } as React.CSSProperties,

  /**
   * Full Width with Bottom Margin - Button with spacing below
   * Use for: Stacked buttons
   */
  fullWidthWithMargin: {
    width: '100%',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Full Width with Small Margin - Button with smaller spacing
   * Use for: Closely stacked buttons
   */
  fullWidthWithSmallMargin: {
    width: '100%',
    marginBottom: '0.5rem',
  } as React.CSSProperties,
};

// ============================================================================
// BUILDING BLOCKS (For custom compositions)
// ============================================================================

export const authLayoutBase = {
  /**
   * Centered Flex Container - Base layout for centering
   * Use for: Custom centered layouts
   */
  centeredFlex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,

  /**
   * Full Viewport Height - Full screen container
   * Use for: Custom full-page layouts
   */
  fullHeight: {
    minHeight: '100vh',
  } as React.CSSProperties,
};

// ============================================================================
// RESPONSIVE VARIATIONS
// ============================================================================

export const authResponsiveStyles = {
  /**
   * Mobile Card Container - Smaller padding for mobile
   * Use for: Mobile-optimized auth pages
   */
  mobileCardContainer: {
    width: '100%',
    maxWidth: '400px',
    padding: '1.5rem',
    backgroundColor: 'var(--surface-color)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-md)',
  } as React.CSSProperties,

  /**
   * Mobile Title - Smaller title for mobile
   * Use for: Mobile-optimized headings
   */
  mobileTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: 'var(--text-primary)',
  } as React.CSSProperties,
};
